import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import {
  MOVEMENT_REASONS,
  ORDER_ONLY_MOVEMENT_REASONS,
  formatQuantity,
  formatWithUnit,
  parseQuantity,
  type RecordMovementInput,
  type StockItem,
} from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import { LoadError } from '../components/feedback/load-error.js';
import { formatCurrency, formatDateTime } from '../lib/format.js';
import { useRecordMovement, useStockItem, useStockMovements } from '../lib/inventory/use-stock.js';

/**
 * One item: what is on the shelf, and every time it moved.
 *
 * The history is the screen. A current quantity is a claim; the rows underneath it are the
 * evidence, and they are shown by default rather than behind a tab because "where did four
 * metres go?" is the question this module exists to answer and hiding the answer one tap away
 * would be a strange way to answer it.
 *
 * The record-a-movement form asks for a REASON first and a quantity second, in that order,
 * because the reason is what makes the number meaningful and because a form that leads with a
 * number invites somebody to type one and pick whatever reason is nearest.
 */

/**
 * The reasons a person records by hand. Issues and returns happen from the order screen, and a
 * counter sale and its reversal from the order's product lines.
 */
type ManualReason = RecordMovementInput['reason'];
const MANUAL_REASONS: readonly ManualReason[] = MOVEMENT_REASONS.filter(
  (reason): reason is ManualReason => !ORDER_ONLY_MOVEMENT_REASONS.includes(reason),
);

export function StockItemPage(): ReactElement {
  const { t } = useTranslation();
  const { itemId = '' } = useParams();
  const item = useStockItem(itemId);
  const movements = useStockMovements(itemId);

  return (
    <>
      <PageHeader
        title={item.data?.name ?? t('stock.title')}
        {...(item.data?.location ? { description: item.data.location } : {})}
        action={
          <Button asChild variant="ghost">
            <Link to="/stock">
              <ArrowLeft aria-hidden="true" />
              {t('stock.backToList')}
            </Link>
          </Button>
        }
      />

      {item.isPending ? <ListSkeleton rows={4} /> : null}

      {item.isError ? <LoadError onRetry={() => void item.refetch()} /> : null}

      {item.data ? (
        <div className="flex flex-col gap-6">
          <OnTheShelf item={item.data} />
          <RecordMovement item={item.data} />

          <section className="flex flex-col gap-2" aria-label={t('stock.history')}>
            <h2 className="text-sm font-semibold">{t('stock.history')}</h2>
            {movements.data && movements.data.items.length > 0 ? (
              <>
                <ul className="flex flex-col gap-1.5">
                  {movements.data.items.map((movement) => (
                    <li
                      key={movement.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium">
                          {t(`stock.reason.${movement.reason}`)}
                          {movement.orderNumber ? ` · ${movement.orderNumber}` : ''}
                        </span>
                        <span className="block text-muted-foreground">
                          {movement.variantColour} · {formatDateTime(movement.occurredAt)}
                          {movement.actorName ? ` · ${movement.actorName}` : ''}
                          {movement.note ? ` · ${movement.note}` : ''}
                        </span>
                      </span>
                      <span className="text-right">
                        <span
                          className={cn(
                            'block font-semibold tabular-nums',
                            movement.quantity < 0 ? 'text-destructive' : 'text-success',
                          )}
                        >
                          {movement.quantity > 0 ? '+' : ''}
                          {formatQuantity(movement.quantity, item.data?.unit ?? 'metre')}
                        </span>
                        <span className="block text-xs text-muted-foreground tabular-nums">
                          {t('stock.balanceAfter', {
                            quantity: formatQuantity(
                              movement.balanceAfter,
                              item.data?.unit ?? 'metre',
                            ),
                          })}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                {/* Shown beside the total on purpose: unfiltered, these two must agree. */}
                <p className="text-xs text-muted-foreground" role="status">
                  {t('stock.historySum', {
                    quantity: formatWithUnit(movements.data.netQuantity, item.data.unit),
                  })}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('stock.noHistory')}</p>
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}

function OnTheShelf({ item }: { item: StockItem }): ReactElement {
  const { t } = useTranslation();

  return (
    <Card className={item.lowStock ? 'border-destructive/40' : ''}>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2">
          {formatWithUnit(item.quantity, item.unit)}
          {item.lowStock ? (
            <Badge variant="destructive">
              <AlertTriangle aria-hidden="true" className="size-3" />
              {t('stock.low')}
            </Badge>
          ) : null}
        </CardTitle>
        <span className="text-sm text-muted-foreground">
          {formatCurrency(item.valuePaise)} · {formatCurrency(item.purchaseRatePaise)}
          {t('stock.perUnit', { unit: item.unit })}
        </span>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-1.5">
          {item.variants
            .filter((variant) => !variant.archived)
            .map((variant) => (
              <li
                key={variant.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-2.5 text-sm"
              >
                <span>
                  <span className="font-medium">{variant.colour}</span>
                  {variant.pattern ? (
                    <span className="text-muted-foreground"> · {variant.pattern}</span>
                  ) : null}
                  {variant.code ? (
                    <span className="text-muted-foreground"> · {variant.code}</span>
                  ) : null}
                </span>
                <span className="font-semibold tabular-nums">
                  {formatWithUnit(variant.quantity, item.unit)}
                </span>
              </li>
            ))}
        </ul>
        {item.reorderLevel > 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {t('stock.reorderLevel', {
              quantity: formatWithUnit(item.reorderLevel, item.unit),
            })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RecordMovement({ item }: { item: StockItem }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordMovement(item.id);

  const live = item.variants.filter((variant) => !variant.archived);
  const [variantId, setVariantId] = useState(live[0]?.id ?? '');
  const [reason, setReason] = useState<ManualReason>('purchase');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [decrease, setDecrease] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function submit(): void {
    setError(null);
    const parsed = parseQuantity(quantity);
    if (parsed === null || parsed <= 0) {
      setError(t('stock.enterQuantity'));
      return;
    }

    record
      .mutateAsync({
        variantId,
        reason,
        quantity: parsed,
        decrease: reason === 'adjustment' ? decrease : false,
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      .then((result) => {
        setQuantity('');
        setNote('');
        toast({
          title: t('stock.movementRecorded', {
            quantity: formatWithUnit(result.item.quantity, item.unit),
          }),
        });
      })
      .catch(() => {
        // The server refuses a movement that would take the shelf negative, and says what is
        // actually there. Showing that is more useful than a generic failure.
        setError(t('stock.movementFailed'));
      });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('stock.recordMovement')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-reason">{t('stock.reasonLabel')}</Label>
          <select
            id="movement-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as ManualReason)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
          >
            {MANUAL_REASONS.map((one) => (
              <option key={one} value={one}>
                {t(`stock.reason.${one}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-variant">{t('stock.colour')}</Label>
          <select
            id="movement-variant"
            value={variantId}
            onChange={(event) => setVariantId(event.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
          >
            {live.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.colour} — {formatWithUnit(variant.quantity, item.unit)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-quantity">{t('stock.quantityLabel', { unit: item.unit })}</Label>
          <Input
            id="movement-quantity"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </div>

        {reason === 'adjustment' ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={decrease}
              onChange={(event) => setDecrease(event.target.checked)}
            />
            {t('stock.adjustmentDecrease')}
          </label>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-note">{t('stock.noteLabel')}</Label>
          <Input
            id="movement-note"
            value={note}
            maxLength={300}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div>
          <Button disabled={record.isPending || live.length === 0} onClick={submit}>
            {t('stock.record')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default StockItemPage;
